import {
  Grid,
  Stack,
  Toolbar,
  Menu,
  MenuItem,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material'
import { SearchDebounce } from './SearchDebounce'
import TableViewIcon from '@mui/icons-material/TableView'
import { useListViewToolbar } from '../../hooks/useListViewToolbar'
import { OriginFilters } from './Filters/OriginFilters'
import { ItemCollectionFilters } from './Filters/ItemCollectionFilters'
import { OriginalTitleFilters } from './Filters/OriginalTitleFilters'
import { ItemTitleFilters } from './Filters/ItemTitleFilters'
import { CreditFilters } from './Filters/CreditFilters'
import { ItemFilters } from './Filters/ItemFilters'
import { FormCode } from '@kistill/shared/types'
import { StickersPopup } from '../Popup/StickersPopup'
import { UpdateItemsLocation } from '../Popup/UpdateItemsLocationPopup'
import { ContentAnalysisFilters } from './Filters/ContentAnalysisFilters'
import { ListActions } from './ListActions'
import { DocumentType } from '@kistill/shared/types'
import { CustomerFilters } from './Filters/CustomerFilters'
import { OnFilterType } from '../../hooks/useHandleOnSubmitFilters'
import { ReactElement } from 'react'

interface ListViewToolbarProps {
  title?: string
  selected: readonly string[]
  setSelected: (selected: Set<string>) => void
  onSearch: (query?: string | null) => void
  onCreate?: () => void
  onFilter: OnFilterType
  type?: string | undefined
  showFilter?: boolean
  handleOpenDocument: (enumType: DocumentType) => void | Promise<void>
  updateListViewData?: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  totalGuids: any
  sortConfig: {
    key: string
    direction: string
  }
  handleOpenItemFiltering: () => void
  columns: { id: string; header: string }[]
  columnVisibility: { [key: string]: boolean }
  setColumnVisibility: (visibility: { [key: string]: boolean }) => void
  initialFilterParams?: Record<string, unknown>
  /** When type is 'items', called before opening change-location popup. Return true to open, false to show error. */
  onOpenChangeLocation?: (selected: string[]) => Promise<boolean>
  /** Called after updating item locations so the list can refetch. */
  refetchList?: () => void | Promise<void>
}

export const ListViewToolbar = ({
  onSearch,
  onCreate,
  onFilter,
  type,
  selected,
  setSelected,
  handleOpenDocument,
  updateListViewData,
  totalGuids,
  sortConfig,
  handleOpenItemFiltering,
  columns,
  columnVisibility,
  setColumnVisibility,
  initialFilterParams,
  onOpenChangeLocation,
  refetchList,
}: ListViewToolbarProps) => {
  const {
    anchorEl,
    openMenu,
    stickersPopUp,
    setStickersPopUp,
    changeLocationPopUp,
    setChangeLocationPopUp,
    columnMenuAnchorEl,
    columnMenuOpen,
    handleClose,
    handleClick,
    handleDocumentAndClose: handleDocumentAndCloseInternal,
    constOpenStickerPopUp,
    constOpenChangeLocationPopUp,
    handleOpenColumnMenu,
    handleCloseColumnMenu,
    getGuids,
  } = useListViewToolbar()

  const handleDocumentAndClose: (enumType: DocumentType) => void = (
    enumType: DocumentType,
  ) => {
    handleDocumentAndCloseInternal(enumType, handleOpenDocument)
  }

  const handleToggleColumn = (columnId: string) => {
    setColumnVisibility({
      ...columnVisibility,
      [columnId]: !(columnVisibility[columnId] ?? true),
    })
  }

  const onOpenChangeLocationClick = () => {
    if (type === 'items' && onOpenChangeLocation) {
      onOpenChangeLocation(selected as string[])
        .then((ok) => {
          if (ok) setChangeLocationPopUp(true)
          handleClose()
        })
        .catch(() => {
          handleClose()
        })
    } else {
      constOpenChangeLocationPopUp()
    }
  }

  // Helper functions for type checks
  const shouldShowFilters = (type?: string): boolean => {
    if (!type) return false
    const hiddenTypes = ['credit-item', 'adminComponent', 'combinedCustomer']
    return !hiddenTypes.includes(type)
  }

  const shouldShowColumnControls = (type?: string): boolean => {
    if (!type) return false
    const hiddenTypes = ['connectedItems', 'combinedCustomer', 'itemLocations']
    return !hiddenTypes.includes(type)
  }

  const isItemType = (type?: string): boolean => {
    if (!type) return false
    return (
      Object.values(FormCode).includes(type as FormCode) ||
      type === 'items' ||
      type === 'connectedItems' ||
      type === 'itemLocations'
    )
  }

  // Filter component mapping
  const getFilterComponent = (type?: string): ReactElement | null => {
    if (!type) return null

    const commonProps = {
      onFilter,
      selected,
      setSelected,
      columnVisibility, // Pass column visibility to filters
      initialFilterParams,
    }

    const filterMap: Record<string, ReactElement> = {
      origin: <OriginFilters {...commonProps} />,
      itemCollection: <ItemCollectionFilters {...commonProps} />,
      originalTitle: <OriginalTitleFilters {...commonProps} />,
      itemTitle: <ItemTitleFilters {...commonProps} />,
      credit: <CreditFilters {...commonProps} />,
      contentAnalysis: <ContentAnalysisFilters {...commonProps} />,
      customer: <CustomerFilters {...commonProps} />,
    }

    // Check if it's an item type (FormCode or special item types)
    if (isItemType(type)) {
      return <ItemFilters {...commonProps} type={type as string} />
    }

    return filterMap[type] || null
  }

  return (
    <Stack>
      <Toolbar
        sx={{
          maxHeight: '25vh',
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        }}
      >
        <Grid container justifyContent={'space-between'}>
          <Grid item xs={6} fontSize='1.2rem'>
            <SearchDebounce onSearch={onSearch} placeholder='leit' />
          </Grid>
        </Grid>
      </Toolbar>
      {shouldShowFilters(type) && (
        <Toolbar
          sx={{
            maxHeight: '100vh',
            mt: 1,
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
          }}
        >
          <Grid container spacing={1} alignItems='flex-end' wrap='nowrap'>
            <Grid item xs alignItems={'center'}>
              {getFilterComponent(type)}
              {stickersPopUp && (
                <StickersPopup
                  setOpen={setStickersPopUp}
                  open={stickersPopUp}
                  handleClose={handleClose}
                  itemGuids={getGuids(selected, totalGuids)}
                  sortConfig={sortConfig}
                />
              )}
              {changeLocationPopUp && (
                <UpdateItemsLocation
                  setOpen={setChangeLocationPopUp}
                  open={changeLocationPopUp}
                  handleClose={handleClose}
                  itemGuids={selected as unknown as string[]}
                  type={type}
                  updateListViewData={updateListViewData}
                  refetchList={refetchList}
                />
              )}
            </Grid>
            {shouldShowColumnControls(type) && type && (
              <>
                <Grid item xs='auto' sx={{ ml: -2 }}>
                  <Tooltip title='Velja dálka'>
                    <IconButton onClick={handleOpenColumnMenu}>
                      <TableViewIcon fontSize='large' />
                    </IconButton>
                  </Tooltip>
                </Grid>
                <Grid item xs='auto' sx={{ ml: -2 }}>
                  <ListActions
                    onCreate={onCreate}
                    handleClick={handleClick}
                    anchorEl={anchorEl}
                    type={type}
                    openMenu={openMenu}
                    handleClose={handleClose}
                    handleDocumentAndClose={handleDocumentAndClose}
                    constOpenStickerPopUp={constOpenStickerPopUp}
                    onOpenChangeLocationClick={onOpenChangeLocationClick}
                    selectedCount={selected.length}
                    handleOpenItemFiltering={handleOpenItemFiltering}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Toolbar>
      )}
      <Menu
        anchorEl={columnMenuAnchorEl}
        open={columnMenuOpen}
        onClose={handleCloseColumnMenu}
      >
        {columns && columns.length > 0 ? (
          columns.map((column) => (
            <MenuItem key={column.id}>
              <Checkbox
                checked={columnVisibility[column.id] ?? true}
                onChange={() => handleToggleColumn(column.id)}
              />
              {column.header}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>Engar dálkar fundust</MenuItem>
        )}
      </Menu>
    </Stack>
  )
}
